/**
 * Value formatters shared by the lead surfaces.
 *
 * Separate from {@link formatCompact} in TrendChart, which abbreviates COUNTS for axis labels.
 * Durations and money need the opposite treatment: a response time of "1.2K" seconds tells nobody
 * anything, and abbreviated revenue is not a figure you can act on.
 */

/**
 * Seconds → the largest two units that matter ("2m 14s", "1h 05m", "3d 4h").
 *
 * Two units, never three: the third is always noise next to the first, and a fixed unit count
 * keeps the column width stable enough to scan down.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  if (seconds < 1) return '<1s';

  const total = Math.round(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
}

/**
 * Money, in full and with its currency.
 *
 * Never abbreviated — see the note on StatTile's string branch. Falls back to a plain grouped
 * number when no currency is known yet, rather than inventing one: a pipeline labelled in the
 * wrong currency is worse than one labelled in none.
 */
export function formatMoney(amount: number | null | undefined, currency: string | null): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';

  const rounded = Math.round(amount * 100) / 100;
  const grouped = rounded.toLocaleString('en-US', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return currency ? `${currency} ${grouped}` : grouped;
}

/** A rate already expressed as 0–1. Null renders as an em dash, never as 0%. */
export function formatPercent(rate: number | null | undefined, digits = 0): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(digits)}%`;
}

/** Short absolute date for a table cell: "2 Aug, 14:05". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
